import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  src: string | null;
  open: boolean;
  onClose: () => void;
}

// Full-screen photo viewer. Shows the original image Grace snapped/uploaded so
// Vince (or Grace) can verify Claude picked the right problem off a page that
// may have several problems on it.
export default function PhotoModal({ src, open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && src && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 bg-ink/90 backdrop-blur p-4 flex flex-col gap-3"
        >
          <div className="flex items-center justify-between">
            <div className="font-display font-black text-white text-lg">
              Original photo
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-10 h-10 rounded-full bg-white/15 text-white hover:bg-white/25 transition flex items-center justify-center"
            >
              ✕
            </button>
          </div>

          <motion.div
            initial={{ scale: 0.96 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.96 }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-h-0 rounded-2xl overflow-auto bg-black/20 flex items-start justify-center"
          >
            <img
              src={src}
              alt="Original problem photo"
              className="max-w-full h-auto object-contain select-none"
              draggable={false}
            />
          </motion.div>

          <div className="text-center text-white/60 text-xs">
            Tap outside the photo or press Esc to close · Pinch to zoom on iPad
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
